// Pure, seed-driven terrain generators for Level.heights. Each function
// returns a flat, row-major heights array (matching Level.heights) with no
// side effects, so they're fully deterministic and unit-testable given a
// fixed seed/rng. See PLAN.md's "Levels" section for the design rationale.

import { createRng } from './rng';

type Rng = ReturnType<typeof createRng>;

/**
 * Generates rolling, connected terrain instead of independent per-tile
 * randomness, by blending two value-noise layers at different scales:
 * a coarse lattice (broad, rolling bumps) interpolated across the whole
 * grid, plus a finer per-tile jitter layer for local detail. This produces
 * "softer" variance than uniform per-tile randomness, which tends to
 * produce isolated single-tile spikes that are awkward to flatten.
 */
export function generateLayeredNoiseHeights(
  rng: Rng,
  size: number,
  floor: number,
  ceiling: number,
  smoothness = 0.7,
): number[] {
  const coarseResolution = Math.max(2, Math.round(size / 4));
  const coarseLattice = buildRandomLattice(rng, coarseResolution);

  const fineWeight = 1 - smoothness;
  const combined: number[] = [];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const coarseValue = sampleBilinear(coarseLattice, coarseResolution, size, x, y);
      const fineValue = rng.next();
      combined.push(coarseValue * smoothness + fineValue * fineWeight);
    }
  }

  const min = Math.min(...combined);
  const max = Math.max(...combined);
  const range = max - min || 1;

  return combined.map((value) => {
    const normalized = (value - min) / range;
    return floor + normalized * (ceiling - floor);
  });
}

function buildRandomLattice(rng: Rng, resolution: number): number[][] {
  const lattice: number[][] = [];
  for (let y = 0; y <= resolution; y += 1) {
    const row: number[] = [];
    for (let x = 0; x <= resolution; x += 1) {
      row.push(rng.next());
    }
    lattice.push(row);
  }
  return lattice;
}

/** Bilinearly samples a coarse `resolution x resolution` lattice at the fractional position tile (x, y) maps to within a `size x size` grid. */
function sampleBilinear(
  lattice: number[][],
  resolution: number,
  size: number,
  x: number,
  y: number,
): number {
  const fx = (x / Math.max(1, size - 1)) * resolution;
  const fy = (y / Math.max(1, size - 1)) * resolution;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(resolution, x0 + 1);
  const y1 = Math.min(resolution, y0 + 1);
  const tx = fx - x0;
  const ty = fy - y0;

  const top = lattice[y0][x0] * (1 - tx) + lattice[y0][x1] * tx;
  const bottom = lattice[y1][x0] * (1 - tx) + lattice[y1][x1] * tx;
  return top * (1 - ty) + bottom * ty;
}

/**
 * Generates a deterministic (seed-independent) stepped pyramid: height
 * decreases in even terraces from the center outward, using Chebyshev
 * distance (concentric squares rather than circles), evoking a ziggurat.
 */
export function generateZigguratHeights(size: number, floor: number, ceiling: number): number[] {
  const heights: number[] = [];
  const center = (size - 1) / 2;
  const maxRing = Math.max(1, Math.ceil(center));
  const range = ceiling - floor;

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const ring = Math.max(Math.abs(x - center), Math.abs(y - center));
      const normalized = 1 - Math.min(1, ring / maxRing);
      heights.push(floor + normalized * range);
    }
  }

  return heights;
}

/**
 * Carves a genuine maze (recursive-backtracker algorithm) into a
 * `(2 * mazeCells + 1) x (2 * mazeCells + 1)` tile grid — the standard
 * "logical cells on odd tiles, walls between them" technique — and maps
 * passage tiles to `floor` height and wall tiles to `ceiling` height, so
 * flattening the level means erasing a real labyrinth pattern.
 */
export function generateMazeHeights(
  rng: Rng,
  mazeCells: number,
  floor: number,
  ceiling: number,
): { heights: number[]; size: number } {
  const size = mazeCells * 2 + 1;
  const passage: boolean[][] = Array.from({ length: size }, () => new Array(size).fill(false) as boolean[]);
  const visited: boolean[][] = Array.from({ length: mazeCells }, () => new Array(mazeCells).fill(false) as boolean[]);

  const carve = (cx: number, cy: number): void => {
    visited[cy][cx] = true;
    const tx = cx * 2 + 1;
    const ty = cy * 2 + 1;
    passage[ty][tx] = true;

    const directions = shuffle(rng, [
      [0, -1],
      [0, 1],
      [-1, 0],
      [1, 0],
    ]);

    for (const [dx, dy] of directions) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= mazeCells || ny >= mazeCells || visited[ny][nx]) {
        continue;
      }
      passage[ty + dy][tx + dx] = true;
      carve(nx, ny);
    }
  };

  carve(0, 0);

  const heights: number[] = [];
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      heights.push(passage[y][x] ? floor : ceiling);
    }
  }

  return { heights, size };
}

/** Fisher-Yates shuffle driven by the level's seeded rng, so maze layouts stay deterministic. */
function shuffle<T>(rng: Rng, items: T[]): T[] {
  const array = [...items];
  for (let index = array.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(rng.next() * (index + 1));
    [array[index], array[swapIndex]] = [array[swapIndex], array[index]];
  }
  return array;
}
